import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { $getAdminAccessInfo } from "~/lib/admin/queries";
import { ThemeToggle } from "~/components/theme-toggle";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { $deleteUserAccount, $updateUserPreferences } from "~/lib/mutations";

export const Route = createFileRoute("/(authenticated)/dashboard/settings/")({
  component: PreferencesSettingsPage,
});

function PreferencesSettingsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { adminDenied?: string };
  const { data: adminInfo } = useQuery({
    queryKey: ["admin-access-info"],
    queryFn: () => $getAdminAccessInfo(),
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = adminInfo?.isAdmin ?? false;

  useEffect(() => {
    if (search.adminDenied === "1") {
      toast.error(
        adminInfo?.adminConfigured
          ? `Admin access denied for ${adminInfo.email}. ADMIN_EMAILS must include this exact address.`
          : "ADMIN_EMAILS is not configured on the server (set wrangler secret for production).",
      );
      navigate({ to: "/dashboard/settings", search: {}, replace: true });
    }
  }, [search.adminDenied, adminInfo, navigate]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleResetOnboarding = async () => {
    try {
      await $updateUserPreferences({
        data: { hasCompletedOnboarding: false, onboardingStep: 0 },
      });
      queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
      toast.success("Onboarding reset successfully");
      navigate({ to: "/onboarding" });
    } catch {
      toast.error("Failed to reset onboarding");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") {
      toast.error('Please type "DELETE" to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const result = await $deleteUserAccount();
      console.log("Delete result:", result);
      toast.success("Account deleted successfully");
      queryClient.clear();
      window.location.href = "/";
    } catch (error) {
      console.error("Delete account error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete account";
      toast.error(errorMessage);
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmation("");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Preferences</h2>
        <p className="text-muted-foreground">Customize your experience</p>
      </div>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="theme">Theme</Label>
              <p className="text-muted-foreground text-sm">Choose your preferred theme</p>
            </div>
            <ThemeToggle />
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Admin</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <Label>Model catalog</Label>
              <p className="text-muted-foreground text-sm">
                Curate recommended models and defaults (models.dev)
              </p>
            </div>
            <Button
              variant="outline"
              render={(props) => (
                <Link {...props} to="/dashboard/admin/models">
                  Open
                </Link>
              )}
            />
          </CardContent>
        </Card>
      )}

      {adminInfo && !adminInfo.isAdmin && (
        <p className="text-muted-foreground text-xs">
          Signed in as <span className="font-mono">{adminInfo.email}</span>
          {!adminInfo.adminConfigured
            ? " — ADMIN_EMAILS is not set on the server."
            : " — not in ADMIN_EMAILS."}
        </p>
      )}

      {/* Sync */}
      <Card>
        <CardHeader>
          <CardTitle>Sync</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Chat sync</Label>
              <p className="text-muted-foreground text-sm">
                Store chats locally by default; optionally share to cloud.
              </p>
            </div>
            <Button
              variant="outline"
              render={(props) => (
                <Link {...props} to="/dashboard/settings/sync">
                  Manage
                </Link>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Reset Onboarding</Label>
              <p className="text-muted-foreground text-sm">
                Go through the onboarding flow again
              </p>
            </div>
            <Button variant="destructive" onClick={handleResetOnboarding}>
              Reset
            </Button>
          </div>

          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-destructive">Delete Account</Label>
                <p className="text-muted-foreground text-sm">
                  Permanently delete your account and all data. This action cannot be
                  undone.
                </p>
              </div>
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger>
                  <Button variant="destructive">Delete Account</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-destructive">Delete Account</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. This will permanently delete your
                      account and remove all your data from our servers.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="delete-confirmation">
                        Type <span className="font-mono">DELETE</span> to confirm
                      </Label>
                      <Input
                        id="delete-confirmation"
                        value={deleteConfirmation}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setDeleteConfirmation(e.target.value)
                        }
                        placeholder=""
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setDeleteConfirmation("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmation !== "DELETE" || isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Delete Account"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
